/**
 * Add Participant Modal
 * =====================
 * 
 * Modal for adding participants to an inbox conversation.
 * Displays company users and allows selecting multiple users.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import { API_ENDPOINTS } from '../../../../utils/config';
import './AddParticipantModal.scss';

interface CompanyUser {
  _id: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
}

interface AddParticipantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (userIds: string[]) => Promise<void>;
  companyId: string;
  existingParticipantIds: string[];
}

const AddParticipantModal: React.FC<AddParticipantModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  companyId,
  existingParticipantIds
}) => {
  const { token } = useAuth();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && companyId) {
      fetchCompanyUsers();
    }
  }, [isOpen, companyId]);

  const fetchCompanyUsers = async () => {
    if (!token || !companyId) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ENDPOINTS.COMPANIES}/${companyId}/users`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Filter out existing participants
        const allUsers = data.data || [];
        const filteredUsers = allUsers.filter((user: CompanyUser) => 
          !existingParticipantIds.includes(user._id)
        );
        setUsers(filteredUsers);
      } else {
        throw new Error(data.message || 'Failed to load users');
      }
    } catch (err: any) {
      console.error('Error fetching company users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleAdd = async () => {
    if (selectedUserIds.length === 0) {
      alert('Please select at least one user');
      return;
    }

    setIsAdding(true);
    try {
      await onAdd(selectedUserIds);
      setSelectedUserIds([]);
      setSearchTerm('');
    } catch (err: any) {
      console.error('Error adding participants:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsAdding(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="add-participant-modal-overlay" onClick={onClose}>
      <div className="add-participant-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-participant-modal__header">
          <h2>Add Participants</h2>
          <button className="add-participant-modal__close" onClick={onClose}>×</button>
        </div>

        <div className="add-participant-modal__content">
          {error && (
            <div className="add-participant-modal__error">
              {error}
            </div>
          )}

          <div className="add-participant-modal__search">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="add-participant-modal__search-input"
            />
          </div>

          {isLoading ? (
            <div className="add-participant-modal__loading">
              Loading users...
            </div>
          ) : (
            <div className="add-participant-modal__users-list">
              {filteredUsers.length === 0 ? (
                <div className="add-participant-modal__empty">
                  {searchTerm ? 'No users found matching your search' : 'No users available to add'}
                </div>
              ) : (
                filteredUsers.map(user => (
                  <div
                    key={user._id}
                    className={`add-participant-modal__user-item ${selectedUserIds.includes(user._id) ? 'selected' : ''}`}
                    onClick={() => handleToggleUser(user._id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(user._id)}
                      onChange={() => handleToggleUser(user._id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="add-participant-modal__user-info">
                      <div className="add-participant-modal__user-name">{user.name}</div>
                      <div className="add-participant-modal__user-email">{user.email}</div>
                      {user.department && (
                        <div className="add-participant-modal__user-department">{user.department}</div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div className="add-participant-modal__footer">
          <button
            className="add-participant-modal__cancel"
            onClick={onClose}
            disabled={isAdding}
          >
            Cancel
          </button>
          <button
            className="add-participant-modal__add"
            onClick={handleAdd}
            disabled={isAdding || selectedUserIds.length === 0}
          >
            {isAdding ? 'Adding...' : `Add ${selectedUserIds.length > 0 ? `(${selectedUserIds.length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddParticipantModal;

